import { Transaction, VoucherCode } from '@/types';
import { v4 as uuidv4 } from 'uuid';

// Função para gerar uma data aleatória nos últimos 90 dias
const getRandomDate = (daysBack = 90) => {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * daysBack));
  return date.toISOString();
};

// Função para gerar uma data de expiração futura (entre 1 e 12 meses)
const getExpirationDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + Math.floor(Math.random() * 12) + 1);
  return date.toISOString();
};

// Função para gerar um número de referência aleatório para transações
const generateReferenceNumber = () => {
  return `TRX-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
};

// Função para gerar um código de voucher aleatório
const generateVoucherCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 12; i++) {
    if (i > 0 && i % 4 === 0) code += '-';
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Organizações de exemplo
const organizations = [
  { id: uuidv4(), name: 'Acme Corporation' },
  { id: uuidv4(), name: 'Globex Industries' },
  { id: uuidv4(), name: 'Stark Enterprises' },
  { id: uuidv4(), name: 'Wayne Industries' },
  { id: uuidv4(), name: 'Umbrella Corporation' },
];

// Clientes de exemplo
const customers = [
  { id: uuidv4(), name: 'João Silva', organization_id: organizations[0].id },
  { id: uuidv4(), name: 'Maria Oliveira', organization_id: organizations[1].id },
  { id: uuidv4(), name: 'Pedro Santos', organization_id: organizations[2].id },
  { id: uuidv4(), name: 'Ana Souza', organization_id: organizations[3].id },
  { id: uuidv4(), name: 'Carlos Ferreira', organization_id: organizations[4].id },
  { id: uuidv4(), name: 'Juliana Costa', organization_id: organizations[0].id },
  { id: uuidv4(), name: 'Roberto Almeida', organization_id: organizations[1].id },
  { id: uuidv4(), name: 'Fernanda Lima', organization_id: organizations[2].id },
];

// Assinaturas de exemplo
const subscriptions = [
  { id: uuidv4(), customer_id: customers[0].id, organization_id: organizations[0].id },
  { id: uuidv4(), customer_id: customers[1].id, organization_id: organizations[1].id },
  { id: uuidv4(), customer_id: customers[2].id, organization_id: organizations[2].id },
  { id: uuidv4(), customer_id: customers[3].id, organization_id: organizations[3].id },
  { id: uuidv4(), customer_id: customers[4].id, organization_id: organizations[4].id },
  { id: uuidv4(), customer_id: customers[5].id, organization_id: organizations[0].id },
  { id: uuidv4(), customer_id: customers[6].id, organization_id: organizations[1].id },
  { id: uuidv4(), customer_id: customers[7].id, organization_id: organizations[2].id },
];

// Métodos de pagamento
const paymentMethods = [
  'credit_card',
  'debit_card',
  'bank_transfer',
  'paypal',
  'pix',
  'boleto',
  'apple_pay',
  'google_pay',
];

// Status de transação
const transactionStatuses: Transaction['status'][] = [
  'pending',
  'completed',
  'failed',
  'refunded',
];

// Moedas
const currencies = ['BRL', 'USD', 'EUR'];

// Exames disponíveis
const exams = [
  'AWS Certified Solutions Architect',
  'Microsoft Azure Administrator',
  'Google Cloud Professional Cloud Architect',
  'Cisco CCNA',
  'CompTIA Security+',
  'Oracle Java Certification',
  'Salesforce Administrator',
  'Project Management Professional (PMP)',
  'Scrum Master Certification',
  'ITIL Foundation',
];

// Status de voucher
const voucherStatuses: VoucherCode['status'][] = [
  'active',
  'used',
  'expired',
  'cancelled',
];

// Gerar transações de exemplo
const generateMockTransactions = (count = 50): Transaction[] => {
  return Array.from({ length: count }, (_, index) => {
    const organization = organizations[Math.floor(Math.random() * organizations.length)];
    const customer = customers[Math.floor(Math.random() * customers.length)];
    const subscription = subscriptions[Math.floor(Math.random() * subscriptions.length)];
    const status = transactionStatuses[Math.floor(Math.random() * transactionStatuses.length)];
    const currency = currencies[Math.floor(Math.random() * currencies.length)];
    const paymentMethod = paymentMethods[Math.floor(Math.random() * paymentMethods.length)];
    const createdAt = getRandomDate();
    const paymentDate = status === 'completed' ? getRandomDate(30) : undefined;
    
    // Gerar valor aleatório entre 100 e 10000
    const amount = Math.floor(Math.random() * 9900) + 100;
    
    return {
      id: uuidv4(),
      organization_id: organization.id,
      customer_id: customer.id,
      subscription_id: subscription.id,
      amount,
      currency,
      payment_method: paymentMethod,
      status,
      reference_number: generateReferenceNumber(),
      description: `Pagamento de assinatura - ${customer.name}`,
      payment_date: paymentDate,
      created_at: createdAt,
      updated_at: createdAt,
      created_by: '00000000-0000-0000-0000-000000000000',
      updated_by: '00000000-0000-0000-0000-000000000000',
      metadata: {
        customer_name: customer.name,
        organization_name: organization.name,
      },
    };
  });
};

// Gerar vouchers de exemplo
const generateMockVoucherCodes = (count = 50): VoucherCode[] => {
  return Array.from({ length: count }, (_, index) => {
    const status = voucherStatuses[Math.floor(Math.random() * voucherStatuses.length)];
    const createdAt = getRandomDate();
    const expiresAt = getExpirationDate();
    const subscription = subscriptions[Math.floor(Math.random() * subscriptions.length)];
    const exam = exams[Math.floor(Math.random() * exams.length)];
    
    // Se o status for 'used', gerar uma data de utilização
    const claimedDate = status === 'used' ? getRandomDate(30) : undefined;
    
    return {
      id: uuidv4(),
      code: generateVoucherCode(),
      exam,
      subscription_id: subscription.id,
      expires_at: expiresAt,
      claimed_date: claimedDate,
      status,
      created_at: createdAt,
      updated_at: createdAt,
      created_by: '00000000-0000-0000-0000-000000000000',
      updated_by: '00000000-0000-0000-0000-000000000000',
    };
  });
};

// Serviço de dados mockados
export const mockDataService = {
  // Transações
  getTransactions: () => generateMockTransactions(),
  createTransaction: (transaction: Partial<Transaction>): Transaction => {
    return {
      id: uuidv4(),
      organization_id: transaction.organization_id || organizations[0].id,
      customer_id: transaction.customer_id || customers[0].id,
      subscription_id: transaction.subscription_id || subscriptions[0].id,
      amount: transaction.amount || 0,
      currency: transaction.currency || 'BRL',
      payment_method: transaction.payment_method || 'credit_card',
      status: transaction.status || 'pending',
      reference_number: transaction.reference_number || generateReferenceNumber(),
      description: transaction.description || 'Nova transação',
      payment_date: transaction.payment_date,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: '00000000-0000-0000-0000-000000000000',
      updated_by: '00000000-0000-0000-0000-000000000000',
    };
  },

  // Vouchers
  getVoucherCodes: () => generateMockVoucherCodes(),
  createVoucherCode: (voucherCode: Partial<VoucherCode>): VoucherCode => {
    return {
      id: uuidv4(),
      code: voucherCode.code || generateVoucherCode(),
      exam: voucherCode.exam || exams[0],
      subscription_id: voucherCode.subscription_id || subscriptions[0].id,
      expires_at: voucherCode.expires_at || getExpirationDate(),
      claimed_date: voucherCode.claimed_date,
      status: voucherCode.status || 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: '00000000-0000-0000-0000-000000000000',
      updated_by: '00000000-0000-0000-0000-000000000000',
    };
  },
  updateVoucherStatus: (id: string, status: VoucherCode['status'], voucherCodes: VoucherCode[]): VoucherCode[] => {
    return voucherCodes.map(voucher => 
      voucher.id === id 
        ? { 
            ...voucher, 
            status, 
            claimed_date: status === 'used' ? new Date().toISOString() : voucher.claimed_date,
            updated_at: new Date().toISOString() 
          } 
        : voucher
    );
  }
};

export default mockDataService; 